import { useContext, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import PropTypes from "prop-types";

import { CartContext } from "@context/CartProvider";
import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { cn } from "@helpers/general";
import { getEligibleCartMetrics, getUpsellTierDiscounts } from "@helpers/cart";
import { useLocalizedSentenceDict } from "@hooks/useSentenceDict";
import { useProductPricing } from "@hooks/usePrices";
import useDiscountsInfo, { DISCOUNT_INFO_MODES } from "@hooks/useDiscountsInfo";

import DISCOUNT_TYPES from "@constants/DiscountTypes";

import UpSellItem from "./upSells/UpSellItem";
import UpSellItemPlaceholder from "./upSells/UpSellItemPlaceholder";

import IconArrowRight from "@svgs/arrow-right.svg";

function UpSells({ title, products = [], isLoading, noNavigation = false, slidesPerView }) {
  const { products: productsWithPrices, isInitialLoading: pricesLoading } = useProductPricing({
    products,
    shouldUseDiscount: true,
  });
  const { cart } = useContext(CartContext);
  const luxeDiscountsInfo = useDiscountsInfo(DISCOUNT_INFO_MODES.LUXE_ONLY);
  const amountPercentTieredDiscount = luxeDiscountsInfo.find(
    ({ type }) => type === DISCOUNT_TYPES.AMOUNT_PERCENT
  );

  const cartLines = cart?.lines || [];
  let eligibleValue = 0;

  const showDynamicPricing = amountPercentTieredDiscount?.discounts?.length;

  if (showDynamicPricing) {
    const { currentEligibleValue } = getEligibleCartMetrics({
      lines: cartLines,
      discountsToCheck: [amountPercentTieredDiscount],
      excludeLuxeItems: true,
    });
    eligibleValue = currentEligibleValue;
  }

  const navigationPrevRef = useRef(null);
  const navigationNextRef = useRef(null);
  const dict = useLocalizedSentenceDict();
  const noProducts = !products.length && !isLoading;

  const swiperSettings = {
    slidesPerView,
  };
  if (!noNavigation) {
    swiperSettings.navigation = {
      nextEl: navigationNextRef.current,
      prevEl: navigationPrevRef.current,
    };
  }

  if (noProducts) {
    return null;
  }

  return (
    <div className={cn("py-[20px] border-b border-t-0 border-light-grey")}>
      <div className="flex justify-between px-[20px] mobile:px-[15px] h-[19px]">
        <p className="typo-body mobile:typo-h3 font-bold">{dict.get(title)}</p>
        <div className="flex gap-[20px]">
          {!noNavigation && (
            <>
              <button
                ref={navigationPrevRef}
                type="button"
                className="rotate-180 disabled:[&>svg>path]:stroke-[#898786] h-[16px]"
                data-testid="upsells-button-prev"
              >
                <IconArrowRight width="8" height="16" fill="none" stroke="#000000" />
              </button>
              <button
                ref={navigationNextRef}
                type="button"
                className="disabled:[&>svg>path]:stroke-[#898786] h-[16px]"
                data-testid="upsells-button-next"
              >
                <IconArrowRight width="8" height="16" fill="none" stroke="#000000" />
              </button>
            </>
          )}
        </div>
      </div>
      <div className="mt-[10px] desktop:mt-[15px]">
        {isLoading || pricesLoading ? (
          <UpSellItemPlaceholder />
        ) : (
          <Swiper
            navigation={swiperSettings.navigation}
            slidesPerView={swiperSettings.slidesPerView}
            className={cn("first:pl-[20px] mobile:first:pl-[15px]")}
          >
            {productsWithPrices.map((product) => {
              const itemFullPrice = Number(product?.full_price ?? product?.fullPrice ?? 0);
              const tierDiscountPercent = showDynamicPricing
                ? getUpsellTierDiscounts({
                    tieredDiscount: amountPercentTieredDiscount,
                    eligibleValue,
                    itemFullPrice,
                  })
                : undefined;

              return (
                <SwiperSlide key={product.handle}>
                  <UpSellItem
                    product={product}
                    pricesLoading={pricesLoading}
                    atcButtonDisabled={product?._upsell?.atcButtonDisabled}
                    tierDiscountPercent={tierDiscountPercent}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        )}
      </div>
    </div>
  );
}

UpSells.propTypes = {
  title: PropTypes.string,
  isLoading: PropTypes.bool,
  products: PropTypes.array,
  noNavigation: PropTypes.bool,
  slidesPerView: PropTypes.number,
};

export default withALErrorBoundary({
  name: "UpSells",
  priority: "P3",
})(UpSells);
